import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { saveUserBet, getRecentHorseBets } from '../services/userDataService'
import { BOOKMAKER_LOGOS, BOOKMAKERS_BY_REGION } from '../config/bookmakers'
import { buildHorseBet, horseBetKey } from './horseBets'

const UNIT_SIZE_KEY = 'bc-horse-unit-size'
const DEFAULT_BOOKMAKER = 'Sportsbet'
const DEFAULT_BONUS_STAKE = 50

const readUnitSize = () => {
  try { return localStorage.getItem(UNIT_SIZE_KEY) || '' } catch { return '' }
}
const writeUnitSize = (v) => {
  try { localStorage.setItem(UNIT_SIZE_KEY, v) } catch {}
}

const unitsStake = (units, unitSize) => {
  const v = Number(units) * Number(unitSize)
  return Number.isFinite(v) && v > 0 ? String(Math.round(v * 100) / 100) : ''
}

export function useHorseTrack() {
  const { user } = useAuth()
  const [tracked, setTracked] = useState(new Set())
  const [tip, setTip] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    getRecentHorseBets(user.id).then(({ data }) => {
      setTracked(new Set((data || []).map(b => horseBetKey(b.bet_data.horse_bot, b.bet_data.horse_market_id))))
    })
  }, [user])

  const isTracked = (t) => tracked.has(horseBetKey(t.bot, t.market_id))

  const open = (t) => {
    const unitSize = readUnitSize()
    setTip(t)
    setForm({
      bookmaker: DEFAULT_BOOKMAKER,
      odds: t.win_odds != null ? String(t.win_odds) : '',
      unit_size: unitSize,
      stake: t.bot === 'ev' ? unitsStake(t.units, unitSize) : String(DEFAULT_BONUS_STAKE),
      free_bonus: false,
    })
  }

  const close = () => {
    setTip(null)
    setForm({})
  }

  const save = async () => {
    if (!user?.id) { alert('Please log in to track bets'); return }
    if (!tip || !(Number(form.odds) > 1) || !(Number(form.stake) > 0)) return
    setSaving(true)
    const { error } = await saveUserBet(user.id, buildHorseBet(tip, form))
    setSaving(false)
    if (error) { alert('Error saving bet. Please try again.'); return }
    if (tip.bot === 'ev' && form.unit_size) writeUnitSize(form.unit_size)
    setTracked(prev => new Set(prev).add(horseBetKey(tip.bot, tip.market_id)))
    close()
  }

  return { isTracked, open, close, save, tip, form, setForm, saving }
}

export default function HorseTrackModal({ tip, form, onFormChange, onConfirm, onClose, saving, badge }) {
  const [bookieOpen, setBookieOpen] = useState(false)
  if (!tip) return null

  const isEv = tip.bot === 'ev'
  const patch = (fields) => onFormChange({ ...form, ...fields })
  const valid = Number(form.odds) > 1 && Number(form.stake) > 0
  const edge = isEv
    ? tip.ev_percentage != null && `${Number(tip.ev_percentage).toFixed(1)}% EV`
    : tip.retention_percentage != null && `${Number(tip.retention_percentage).toFixed(1)}% retention`

  return (
    <>
      <div className="bc-overlay" onClick={onClose} />
      <div className="ptm-modal">
        <div className="ptm-header">
          <span className="ptm-title">Track Bet</span>
          <button className="bc-panel-close" onClick={onClose}>×</button>
        </div>

        <div className="ptm-info-card" style={{ gap: '14px' }}>
          {badge}
          <div className="ptm-info-body">
            <div className="ptm-player-row">
              <span className="ptm-player-name">{tip.horse_name}</span>
              {edge && (
                <span style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 700, color: '#34d399', backgroundColor: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)', whiteSpace: 'nowrap' }}>
                  {edge}
                </span>
              )}
            </div>
            <div className="ptm-info-sub">{tip.venue} R{tip.race_number} · {new Date(tip.start_time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
            <div className="ptm-info-meta">
              {isEv ? 'Promo Bet · bonus back if 2nd or 3rd' : 'Bonus Turnover · bonus bet, no lay'}
            </div>
          </div>
        </div>

        <div className="ptm-form">
          <div className="ptm-field-full">
            <label className="ptm-label">Bookmaker</label>
            <div className="ptm-bookie-select">
              <button type="button" className={`ptm-bookie-trigger${bookieOpen ? ' open' : ''}`} onClick={() => setBookieOpen(o => !o)}>
                {BOOKMAKER_LOGOS[form.bookmaker] && <img src={BOOKMAKER_LOGOS[form.bookmaker]} alt="" className="ptm-bookie-logo" />}
                <span className="ptm-bookie-name">{form.bookmaker}</span>
                <svg className={`ptm-bookie-chevron${bookieOpen ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {bookieOpen && (
                <>
                  <div className="ptm-bookie-backdrop" onClick={() => setBookieOpen(false)} />
                  <div className="ptm-bookie-dropdown">
                    {BOOKMAKERS_BY_REGION.AU.map(name => (
                      <button
                        key={name}
                        type="button"
                        className={`ptm-bookie-option${form.bookmaker === name ? ' active' : ''}`}
                        onClick={() => { patch({ bookmaker: name }); setBookieOpen(false) }}
                      >
                        {BOOKMAKER_LOGOS[name] ? <img src={BOOKMAKER_LOGOS[name]} alt="" className="ptm-bookie-logo" /> : <div className="ptm-bookie-logo-placeholder" />}
                        <span className="ptm-bookie-name">{name}</span>
                        {form.bookmaker === name && (
                          <svg className="ptm-bookie-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {isEv && (
            <div className="ptm-form-row">
              <div className="ptm-field">
                <label className="ptm-label">Unit size ($)</label>
                <input
                  type="number"
                  step="1"
                  placeholder="10"
                  value={form.unit_size ?? ''}
                  onChange={e => patch({ unit_size: e.target.value, stake: unitsStake(tip.units, e.target.value) })}
                  className="ptm-input"
                />
              </div>
              <div className="ptm-field">
                <label className="ptm-label">Units</label>
                <div className="ptm-input" style={{ color: '#94a3b8', cursor: 'default' }}>{tip.units ?? '–'}</div>
              </div>
            </div>
          )}

          <div className="ptm-form-row">
            <div className="ptm-field">
              <label className="ptm-label">Odds</label>
              <input type="number" step="0.01" value={form.odds ?? ''} onChange={e => patch({ odds: e.target.value })} className="ptm-input" />
            </div>
            <div className="ptm-field">
              <label className="ptm-label">{isEv ? 'Stake ($)' : 'Bonus bet ($)'}</label>
              <input type="number" step="0.01" value={form.stake ?? ''} onChange={e => patch({ stake: e.target.value })} className="ptm-input" />
            </div>
          </div>

          {!isEv && (
            <label className="ptm-check">
              <input type="checkbox" checked={!!form.free_bonus} onChange={e => patch({ free_bonus: e.target.checked })} />
              <span>Free bonus (no qualifying bet)</span>
            </label>
          )}
        </div>

        <div className="ptm-footer">
          <button className="bc-btn ghost" onClick={onClose}>Cancel</button>
          <div className="ptm-footer-right">
            <button className="bc-btn primary" onClick={onConfirm} disabled={!valid || saving}>
              {saving ? 'Saving…' : 'Track'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
